import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthUser, LoginResponse, AuthConfigResponse, AuthMode } from '@uniticket/shared';
import { apiFetch, setAccessToken, ApiError, API_PREFIX } from '@/lib/api-client';

// Two auth modes, chosen by the backend (AUTH_MODE) and surfaced at GET /auth/config:
//   • 'password' — the app's own single-step email+password login (staging/dev), with silent-refresh restore.
//   • 'proxy'    — a forward-auth reverse proxy (prod: Nginx + a central EntraID service) authenticates
//                  upstream and injects the identity on every request; the SPA does NO login, it just asks
//                  /auth/me who it is. "Authenticated upstream but not provisioned here" → `forbidden` (an
//                  access-denied screen), NOT a login form.
// 2FA/TOTP, recovery codes and the app-driven Entra OIDC button are out of scope here.

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'forbidden' }
  | { status: 'authenticated'; user: AuthUser };

interface AuthContextType {
  state: AuthState;
  user: AuthUser | null;
  mode: AuthMode;
  isAdmin: boolean;
  isInternal: boolean;
  /** Compte Build (user 2026-08-07) : la console DMP et rien d'autre. */
  isBuild: boolean;
  // DMP-write beta (Option B): true when the SPA must confirm before a real-DMP write (temporary guardrail).
  dmpWriteConfirm: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [mode, setMode] = useState<AuthMode>('password');
  const [dmpWriteConfirm, setDmpWriteConfirm] = useState(false);
  const user = state.status === 'authenticated' ? state.user : null;

  // Refs so the stable logout() callback reads fresh values without going stale.
  const authTypeRef = useRef<string | undefined>(undefined);
  authTypeRef.current = user?.authType;
  const modeRef = useRef<AuthMode>('password');
  modeRef.current = mode;
  const logoutUrlRef = useRef<string | null>(null);

  // Apply the user's saved language preference ONCE per authenticated session (on login / restore).
  // A ref guard keeps this from re-running and fighting the manual EN|FR toggle (react-i18next re-renders
  // this provider on every languageChanged). Reset on logout so the next login re-applies the preference.
  const { i18n } = useTranslation();
  const langSyncedRef = useRef(false);
  useEffect(() => {
    if (!user) { langSyncedRef.current = false; return; }
    if (user.languagePref && !langSyncedRef.current) {
      langSyncedRef.current = true;
      i18n.changeLanguage(user.languagePref);
    }
  }, [user, i18n]);

  // On mount: learn the auth mode, then restore the session accordingly.
  useEffect(() => {
    let cancelled = false;
    async function init() {
      // 1. Public: which auth mode is the backend in?
      let cfg: AuthConfigResponse = { mode: 'password', ssoEnabled: false, logoutUrl: null, dmpWrite: false, dmpWriteConfirm: false };
      try {
        const r = await fetch(`${API_PREFIX}/auth/config`);
        if (r.ok) cfg = await r.json();
      } catch {
        // Network hiccup → assume password mode (the safe, login-capable default).
      }
      if (cancelled) return;
      setMode(cfg.mode);
      setDmpWriteConfirm(!!cfg.dmpWriteConfirm);
      logoutUrlRef.current = cfg.logoutUrl;

      if (cfg.mode === 'proxy') {
        // The proxy attaches identity to every request — just ask who we are.
        try {
          const { user: authUser } = await apiFetch<{ user: AuthUser }>('/auth/me');
          if (!cancelled) setState({ status: 'authenticated', user: authUser });
        } catch {
          // 401 (no header — shouldn't happen behind the proxy) or 403 (not provisioned) → access-denied.
          if (!cancelled) setState({ status: 'forbidden' });
        }
        return;
      }

      // password mode: silent refresh → me.
      try {
        const res = await fetch(`${API_PREFIX}/auth/refresh`, { method: 'POST', credentials: 'include' });
        if (!res.ok) {
          if (!cancelled) setState({ status: 'unauthenticated' });
          return;
        }
        const { accessToken } = await res.json();
        setAccessToken(accessToken);
        const { user: authUser } = await apiFetch<{ user: AuthUser }>('/auth/me');
        if (!cancelled) setState({ status: 'authenticated', user: authUser });
      } catch {
        if (!cancelled) setState({ status: 'unauthenticated' });
      }
    }
    init();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${API_PREFIX}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({ message: res.statusText }));
      throw new ApiError(res.status, data.message || res.statusText);
    }
    const data: LoginResponse = await res.json();
    if (data.step === 'AUTHENTICATED') {
      setAccessToken(data.accessToken);
      setState({ status: 'authenticated', user: data.user });
    } else {
      // The standalone backend never issues a multi-step login; treat anything else as an error.
      throw new ApiError(500, 'Unexpected login step');
    }
  }, []);

  const updateUser = useCallback((updatedUser: AuthUser) => {
    setState({ status: 'authenticated', user: updatedUser });
  }, []);

  const logout = useCallback(async () => {
    setAccessToken(null);
    // Proxy mode: hand off to the proxy/Entra federated sign-out (the app can't end that session itself).
    if (modeRef.current === 'proxy') {
      window.location.href = logoutUrlRef.current || import.meta.env.BASE_URL;
      return;
    }
    // Pure-Entra (app-driven OIDC) accounts: federated Microsoft sign-out, which clears the refresh cookie
    // server-side AND ends the Microsoft session, then redirects to /login.
    if (authTypeRef.current === 'ENTRA_ID') {
      window.location.href = `${API_PREFIX}/auth/sso/logout`;
      return;
    }
    try {
      await fetch(`${API_PREFIX}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // Ignore — we're logging out anyway.
    }
    setState({ status: 'unauthenticated' });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        state,
        user,
        mode,
        isAdmin: user?.role === 'SYSTEM_ADMIN',
        isInternal: user?.role === 'SYSTEM_ADMIN' || user?.role === 'UNIFIBER_OPERATOR',
        isBuild: user?.role === 'BUILD',
        dmpWriteConfirm,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
